import { PriceRangeApiDto, PriceSkuApiDto } from '@b2x/storefront-api-js-client/src/';
import React from 'react';

import { appConfig } from './config';

export interface UsePriceOptions {
  multiplier?: number; // Posso gestire correttamente il multiplier solo sui valori "unrounded" (non arrotondati).
}

export interface UsePriceResult {
  bestPriceDiscountPercentage?: number;
  bestPriceDiscountPercentageDecimal?: number;
  bestPriceValue?: number;
  crossedOutPrice?: number;
  discountPercentage?: number;
  discountPercentageDecimal?: number;
  isPriceInRange: boolean;
  perUnit?: {
    crossedOutPrice?: number;
    measurement?: string;
    price?: number;
  };
  price: number;
  priveVatIncluded?: number;
  vat?: number;
}

export const usePrice = (
  priceRangeApiDto?: PriceRangeApiDto,
  priceSkuApiDto?: PriceSkuApiDto,
  options?: UsePriceOptions
): UsePriceResult => {
  const { multiplier = 1 } = options ?? {};

  const { enablePricesWithExplicitTaxes } = appConfig;

  const isPriceInRange = React.useMemo(
    () => priceSkuApiDto === undefined && priceRangeApiDto?.from.value !== priceRangeApiDto?.to.value,
    [priceRangeApiDto?.from.value, priceRangeApiDto?.to.value, priceSkuApiDto]
  );

  const crossedOutPrice = React.useMemo(() => {
    const _price = priceSkuApiDto ? priceSkuApiDto : priceRangeApiDto ? priceRangeApiDto.from : undefined;
    if (
      enablePricesWithExplicitTaxes &&
      _price?.crossedOutValueTaxable !== undefined &&
      _price.crossedOutValueTaxableUnrounded !== undefined
    ) {
      return multiplier > 1 ? _price.crossedOutValueTaxableUnrounded * multiplier : _price.crossedOutValueTaxable;
    } else if (_price?.crossedOutValue !== undefined && _price.crossedOutValueUnrounded !== undefined) {
      return multiplier > 1 ? _price.crossedOutValueUnrounded * multiplier : _price.crossedOutValue;
    } else {
      return undefined;
    }
  }, [enablePricesWithExplicitTaxes, multiplier, priceRangeApiDto, priceSkuApiDto]);

  const discountPercentage = React.useMemo(
    () => (priceSkuApiDto ? priceSkuApiDto.discountPercentage : priceRangeApiDto?.from.discountPercentage),
    [priceSkuApiDto, priceRangeApiDto?.from.discountPercentage]
  );

  const discountPercentageDecimal = React.useMemo(
    () => discountPercentage && Math.round(discountPercentage * 100),
    [discountPercentage]
  );

  const price = React.useMemo(() => {
    const _price = priceSkuApiDto ? priceSkuApiDto : priceRangeApiDto ? priceRangeApiDto.from : undefined;

    if (enablePricesWithExplicitTaxes && _price && _price.valueTaxableUnrounded && _price.valueTaxable) {
      // !NOTE: nel caso in cui non mi tornano tutti i prezzi senza iva mostro quello normale
      return multiplier > 1 ? _price.valueTaxableUnrounded * multiplier : _price.valueTaxable;
    } else if (_price) {
      return multiplier > 1 ? _price.valueUnrounded * multiplier : _price.value;
    } else {
      return 0;
    }
  }, [enablePricesWithExplicitTaxes, multiplier, priceRangeApiDto, priceSkuApiDto]);

  const priveVatIncluded = React.useMemo(() => {
    const _price = priceSkuApiDto ? priceSkuApiDto : priceRangeApiDto ? priceRangeApiDto.from : undefined;

    if (_price) {
      return multiplier > 1 ? _price.valueUnrounded * multiplier : _price.value;
    } else {
      return 0;
    }
  }, [multiplier, priceRangeApiDto, priceSkuApiDto]);

  const perUnit = React.useMemo(
    () =>
      priceSkuApiDto
        ? {
            crossedOutPrice: enablePricesWithExplicitTaxes
              ? priceSkuApiDto.crossedOutValueTaxablePerUnit
              : priceSkuApiDto.crossedOutValuePerUnit,
            measurement: priceSkuApiDto.measurementUnit,
            price: enablePricesWithExplicitTaxes ? priceSkuApiDto.valueTaxablePerUnit : priceSkuApiDto.valuePerUnit,
          }
        : undefined,
    [enablePricesWithExplicitTaxes, priceSkuApiDto]
  );

  const vat = React.useMemo(() => {
    const _vat = priceSkuApiDto ? priceSkuApiDto.vat : priceRangeApiDto?.from.vat;
    // return _vat ? _vat * multiplier : 0;
    return _vat;
  }, [priceRangeApiDto?.from.vat, priceSkuApiDto]);

  const bestPriceValue = React.useMemo(() => {
    const _bestPriceValue = priceSkuApiDto?.bestPriceValue;
    // return _bestPriceValue ? _bestPriceValue * multiplier : undefined;
    return _bestPriceValue;
  }, [priceSkuApiDto?.bestPriceValue]);

  const bestPriceDiscountPercentage = React.useMemo(
    () => priceSkuApiDto?.bestPriceDiscountPercentage,
    [priceSkuApiDto?.bestPriceDiscountPercentage]
  );

  const bestPriceDiscountPercentageDecimal = React.useMemo(
    () => bestPriceDiscountPercentage && Math.round(bestPriceDiscountPercentage * 100),
    [bestPriceDiscountPercentage]
  );

  return {
    bestPriceDiscountPercentage,
    bestPriceDiscountPercentageDecimal,
    bestPriceValue,
    crossedOutPrice,
    discountPercentage,
    discountPercentageDecimal,
    isPriceInRange,
    perUnit,
    price,
    priveVatIncluded,
    vat,
  };
};
